import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import client from '@/apolloClient';
import type { PerformanceResultType } from '@/gql/enums';
import { ImportHistoryStatus } from '@/gql/enums';
import { error as showErrorNotification } from '@/utils/notifications/actions';
import { ImportHistory, ImportPerformanceResultMutation } from '../bulk/BulkModification.graphql';

const pollMs = 1000;

const useImportPerformance = () => {
    const { t } = useTranslation('performanceResult');

    const importAndPoll = useCallback(
        async (resultType: PerformanceResultType, file: File) => {
            // 1) Kick off import (returns historyId)
            const { data } = await client.mutate({
                mutation: ImportPerformanceResultMutation,
                variables: { resultType, file, size: file.size },
            });

            if (data?.importPerformanceResult?.__typename === 'ErrorResponse') {
                const errorResponse = data.importPerformanceResult;
                const specificErrorField = errorResponse.fields?.find(f => f.message);
                const errorMessage = specificErrorField?.message || t('bulk.importPerformanceResultErrorDesc');
                showErrorNotification({
                    message: t('bulk.importPerformanceResultErrorTitle'),
                    description: errorMessage,
                });
                return 'error';
            }

            const historyId = data?.importPerformanceResult?.result?.historyId;
            if (!historyId) {
                return 'error';
            }

            // 2) Poll import history until status is not Pending
            // NOTE: query name/types originate from codegen on BulkModification.graphql
            let status: ImportHistoryStatus = ImportHistoryStatus.Pending;
            while (status === ImportHistoryStatus.Pending || status === ImportHistoryStatus.InProgress) {
                const { data: h } = await client.query({
                    query: ImportHistory,
                    variables: { id: historyId },
                    fetchPolicy: 'no-cache',
                });
                status =
                    h?.importHistory?.__typename === 'ImportHistory'
                        ? (h.importHistory.status as ImportHistoryStatus)
                        : ImportHistoryStatus.Pending;
                if (
                    status === ImportHistoryStatus.Completed ||
                    status === ImportHistoryStatus.Failed ||
                    status === ImportHistoryStatus.Paused
                ) {
                    break;
                }
                await new Promise(r => setTimeout(r, pollMs));
            }

            return status === ImportHistoryStatus.Completed ? 'success' : 'error';
        },
        [t]
    );

    return { importAndPoll };
};

export default useImportPerformance;
